import React, { useState, useEffect } from 'react';
import { getMessagingSettings, testMessaging } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    Gear,
    WhatsappLogo,
    ChatCircleText,
    Envelope,
    CheckCircle,
    XCircle,
    PaperPlaneTilt,
    ArrowClockwise,
    Phone,
    Lightning
} from '@phosphor-icons/react';

const SettingsPage = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testingChannel, setTestingChannel] = useState(null);
    const [testPhone, setTestPhone] = useState('');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await getMessagingSettings();
            setSettings(response.data);
        } catch (error) {
            toast.error('Error al cargar configuración');
        } finally {
            setLoading(false);
        }
    };

    const handleTest = async (channel) => {
        if (!testPhone) {
            toast.error('Ingresa un número de teléfono para la prueba');
            return;
        }
        setTestingChannel(channel);
        try {
            const response = await testMessaging(channel, testPhone);
            toast.success(`Mensaje de prueba enviado por ${channel.toUpperCase()}`);
            fetchSettings();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Error al enviar prueba por ${channel}`);
        } finally {
            setTestingChannel(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2D4B]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="settings-page">
            {/* Header */}
            <div>
                <h1 className="font-editorial text-2xl sm:text-3xl font-medium text-[#1F2D4B]">
                    Configuración
                </h1>
                <p className="text-[#4B5878] mt-1">
                    Gestiona la conectividad y canales de mensajería
                </p>
            </div>

            {/* Connection Status Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${settings?.twilio?.sms_configured ? 'bg-[#D1FAE5]' : 'bg-red-50'}`}>
                                <ChatCircleText size={22} weight="duotone" className={settings?.twilio?.sms_configured ? 'text-[#059669]' : 'text-red-500'} />
                            </div>
                            <div>
                                <p className="font-medium text-[#1F2D4B] text-sm">SMS</p>
                                {settings?.twilio?.sms_configured ? (
                                    <Badge className="bg-[#D1FAE5] text-[#059669] border-0 text-xs mt-0.5">Conectado</Badge>
                                ) : (
                                    <Badge className="bg-red-50 text-red-600 border-0 text-xs mt-0.5">No configurado</Badge>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${settings?.aurora?.whatsapp_configured ? 'bg-[#D1FAE5]' : 'bg-red-50'}`}>
                                <WhatsappLogo size={22} weight="duotone" className={settings?.aurora?.whatsapp_configured ? 'text-[#25D366]' : 'text-red-500'} />
                            </div>
                            <div>
                                <p className="font-medium text-[#1F2D4B] text-sm">WhatsApp</p>
                                {settings?.aurora?.whatsapp_configured ? (
                                    <Badge className="bg-[#D1FAE5] text-[#059669] border-0 text-xs mt-0.5">Aurora</Badge>
                                ) : (
                                    <Badge className="bg-red-50 text-red-600 border-0 text-xs mt-0.5">No configurado</Badge>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${settings?.sendgrid?.connected ? 'bg-[#D1FAE5]' : 'bg-amber-50'}`}>
                                <Envelope size={22} weight="duotone" className={settings?.sendgrid?.connected ? 'text-[#059669]' : 'text-amber-600'} />
                            </div>
                            <div>
                                <p className="font-medium text-[#1F2D4B] text-sm">Email</p>
                                {settings?.sendgrid?.connected ? (
                                    <Badge className="bg-[#D1FAE5] text-[#059669] border-0 text-xs mt-0.5">SendGrid</Badge>
                                ) : (
                                    <Badge className="bg-amber-50 text-amber-700 border-0 text-xs mt-0.5">Pendiente</Badge>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Twilio Configuration - SMS */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="font-editorial text-lg text-[#1F2D4B] flex items-center gap-2">
                            <ChatCircleText size={20} weight="duotone" className="text-[#1F2D4B]" />
                            Twilio - SMS
                        </CardTitle>
                        <Badge className={`border-0 ${settings?.twilio?.connected ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-red-50 text-red-600'}`}>
                            {settings?.twilio?.connected ? 'Conectado' : 'Desconectado'}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle size={16} weight="fill" className="text-[#059669]" />
                                <span className="text-sm font-medium text-[#1F2D4B]">Account SID</span>
                            </div>
                            <p className="text-sm text-[#4B5878] font-mono">
                                {settings?.twilio?.account_sid || 'No configurado'}
                            </p>
                        </div>
                        <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                            <div className="flex items-center gap-2 mb-2">
                                {settings?.twilio?.sms_configured ? (
                                    <CheckCircle size={16} weight="fill" className="text-[#059669]" />
                                ) : (
                                    <XCircle size={16} weight="fill" className="text-red-500" />
                                )}
                                <span className="text-sm font-medium text-[#1F2D4B]">Número SMS</span>
                            </div>
                            <p className="text-sm text-[#4B5878] font-mono">
                                {settings?.twilio?.sms_number || 'No configurado'}
                            </p>
                        </div>
                    </div>

                    {/* SMS Test */}
                    <div className="pt-4 border-t border-[#ECE3D2]">
                        <p className="text-sm font-medium text-[#1F2D4B] mb-3">Enviar SMS de prueba</p>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1">
                                <Input
                                    placeholder="+52 1 33 4411 6061"
                                    value={testPhone}
                                    onChange={(e) => setTestPhone(e.target.value)}
                                    data-testid="test-phone-input"
                                    className="h-11 rounded-xl border-[#ECE3D2] bg-white text-[#1F2D4B] placeholder:text-[#9CA3AF]"
                                />
                            </div>
                            <Button
                                onClick={() => handleTest('sms')}
                                disabled={testingChannel === 'sms' || !settings?.twilio?.sms_configured}
                                data-testid="test-sms-btn"
                                className="h-11 bg-[#1F2D4B] hover:bg-[#2D3A5A] text-white rounded-xl"
                            >
                                <ChatCircleText size={18} className="mr-2" />
                                {testingChannel === 'sms' ? 'Enviando...' : 'Probar SMS'}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Aurora Configuration - WhatsApp */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="font-editorial text-lg text-[#1F2D4B] flex items-center gap-2">
                            <WhatsappLogo size={20} weight="duotone" className="text-[#25D366]" />
                            Aurora Inbox - WhatsApp
                        </CardTitle>
                        <Badge className={`border-0 ${settings?.aurora?.connected ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-red-50 text-red-600'}`}>
                            {settings?.aurora?.connected ? 'Conectado' : 'Desconectado'}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                            <div className="flex items-center gap-2 mb-2">
                                {settings?.aurora?.connected ? (
                                    <CheckCircle size={16} weight="fill" className="text-[#059669]" />
                                ) : (
                                    <XCircle size={16} weight="fill" className="text-red-500" />
                                )}
                                <span className="text-sm font-medium text-[#1F2D4B]">Canal WhatsApp</span>
                            </div>
                            <p className="text-sm text-[#4B5878]">
                                {settings?.aurora?.connected ? 'WhatsApp Web conectado vía Aurora' : 'No configurado'}
                            </p>
                        </div>
                        <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                            <div className="flex items-center gap-2 mb-2">
                                <Lightning size={16} weight="fill" className="text-[#FB7185]" />
                                <span className="text-sm font-medium text-[#1F2D4B]">Estadísticas</span>
                            </div>
                            <p className="text-sm text-[#4B5878]">
                                {settings?.stats?.total_sent || 0} enviados / {settings?.stats?.total_failed || 0} fallidos
                            </p>
                        </div>
                    </div>

                    {/* WhatsApp Test */}
                    <div className="pt-4 border-t border-[#ECE3D2]">
                        <p className="text-sm font-medium text-[#1F2D4B] mb-3">Enviar WhatsApp de prueba</p>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1">
                                <Input
                                    placeholder="+52 1 33 4408 9944"
                                    value={testPhone}
                                    onChange={(e) => setTestPhone(e.target.value)}
                                    data-testid="test-whatsapp-phone-input"
                                    className="h-11 rounded-xl border-[#ECE3D2] bg-white text-[#1F2D4B] placeholder:text-[#9CA3AF]"
                                />
                            </div>
                            <Button
                                onClick={() => handleTest('whatsapp')}
                                disabled={testingChannel === 'whatsapp' || !settings?.aurora?.connected}
                                data-testid="test-whatsapp-btn"
                                className="h-11 bg-[#25D366] hover:bg-[#1da851] text-white rounded-xl"
                            >
                                <WhatsappLogo size={18} className="mr-2" />
                                {testingChannel === 'whatsapp' ? 'Enviando...' : 'Probar WhatsApp'}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* SendGrid Configuration - Email */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="font-editorial text-lg text-[#1F2D4B] flex items-center gap-2">
                            <Envelope size={20} weight="duotone" className="text-[#FB7185]" />
                            SendGrid - Email
                        </CardTitle>
                        <Badge className={`border-0 ${settings?.sendgrid?.connected ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-amber-50 text-amber-700'}`}>
                            {settings?.sendgrid?.connected ? 'Conectado' : 'Pendiente'}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    {settings?.sendgrid?.connected ? (
                        <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle size={16} weight="fill" className="text-[#059669]" />
                                <span className="text-sm font-medium text-[#1F2D4B]">Email remitente</span>
                            </div>
                            <p className="text-sm text-[#4B5878] font-mono">{settings.sendgrid.from_email}</p>
                        </div>
                    ) : (
                        <div className="p-4 rounded-xl bg-amber-50/50 border border-amber-200">
                            <p className="text-sm text-amber-800">
                                Configuración de SendGrid pendiente.
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Recent Messages Log */}
            {settings?.recent_messages?.length > 0 && (
                <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="font-editorial text-lg text-[#1F2D4B]">
                                Historial de Mensajes
                            </CardTitle>
                            <Button
                                onClick={fetchSettings}
                                variant="ghost"
                                size="sm"
                                className="text-[#4B5878] hover:text-[#1F2D4B]"
                            >
                                <ArrowClockwise size={16} />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                            {settings.recent_messages.map((msg, index) => (
                                <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-[#ECE3D2]">
                                        {msg.type === 'sms' && <ChatCircleText size={16} className="text-[#1F2D4B]" />}
                                        {msg.type === 'whatsapp' && <WhatsappLogo size={16} className="text-[#25D366]" />}
                                        {msg.type === 'email' && <Envelope size={16} className="text-[#FB7185]" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-[#1F2D4B] truncate">{msg.to}</p>
                                        <p className="text-xs text-[#4B5878] truncate">{msg.body || msg.subject}</p>
                                    </div>
                                    <Badge className={`border-0 text-xs ${
                                        msg.status === 'sent' ? 'bg-[#D1FAE5] text-[#059669]' : 
                                        msg.status === 'failed' ? 'bg-red-50 text-red-600' : 
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                        {msg.status === 'sent' ? 'Enviado' : msg.status === 'failed' ? 'Fallido' : 'Pendiente'}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default SettingsPage;
